import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type ActiveIncentiveView, type ActiveIncentivesListView, type AdminSmartCampaignsView, type SmartCampaignEndReason, type SmartCampaignView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  maySelfApprove,
  EMPTY_OFFER_DRAFT,
  activeIncentiveAuthority,
  bpToPercentText,
  formatMarketInstant,
  groupIncentiveZones,
  marketTimeZone,
  offerApproveBlock,
  offerCanEnd,
  offerDraftIssue,
  offerProposalBody,
  offerSpendBp,
  offerStateTone,
  percentTextToBp,
  policyStateTone,
  windowChoices,
  type OfferDraft,
  tailOf,
} from '../../src/index';
import { useAdmin } from '../session';
import { usePersonNames } from '../components/person-name';
import { GuaranteeOffersPanel } from './GuaranteeOffersPanel';
import { DeliverySubsidyOffersPanel } from './DeliverySubsidyOffersPanel';
import { SmartCampaignsPanel } from './SmartCampaignsPanel';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, Badge, Button, EmptyState, Field, FormRow, Meter, MoneyInput, Notice, PageHeader, Panel, Row, Select, Stack, Table, Text, TextInput, useAutoRefresh } from '@vello/web-theme';

/**
 * Finance › Ofertas de incentivo (W10-RIDER-ZONE-DISCOUNT, D490, ADR-0106; doc 17 section 3.4), over `ActiveIncentivesAdminApi` (`/v1/admin/active-incentives/*`).
 *
 * For the market in the header scope (never typed):
 * - **the bounds**: whether the market publishes the lever policy (`incentives.riderDiscountPolicy`) and an open `incentives.fundPolicy` - NO policy, NO offer - and the
 *   maximums the policy allows (the form never offers a value past them; the server re-checks every one);
 * - **the offers**: in force, scheduled and past, with their zone, period in the market's own time zone, how much of the offer's budget is used, and who proposed and
 *   decided them;
 * - **FOUR EYES**: the maker schedules an offer (a zone or the whole market, a discount, a cap per ride, a budget, a duration from a closed list, a reason from a closed
 *   list), a SECOND operator approves or refuses it (Approve is hidden from the proposer, with the reason shown), and any operator holding the word can END an offer in
 *   force with one click - rides already promised keep the discount they were quoted.
 * Money is typed through the design-system `MoneyInput` in the policy's currency (never minor units, never a typed currency). A refused or failed read is an ERROR - never an
 * empty list. Every code shown (lever, state, reason, refusal) is read through the central vocabulary. No native dialog is ever opened. The list refreshes itself.
 */

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string } | undefined;

const note = (text: string): ReactElement => <EmptyState title={text} />;

export function ActiveIncentivesScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  const { mayView, mayManage, mayApprove } = activeIncentiveAuthority(grants);
  const { market: scopeMarket, choices } = useScopeMarket();
  const market = scopeMarket ?? '';
  const [loaded, setLoaded] = useState('');
  const [view, setView] = useState<ActiveIncentivesListView | undefined>(undefined);
  const personName = usePersonNames((view?.incentives ?? []).map((o) => o.createdBy.id));
  const [campaigns, setCampaigns] = useState<AdminSmartCampaignsView | undefined>(undefined);
  const [zoneNames, setZoneNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<OfferDraft>(EMPTY_OFFER_DRAFT);
  const [percentText, setPercentText] = useState('');
  const seq = useRef(0);

  const refusal = useCallback(
    (e: { code: string; reasonCode?: string; detail?: string }): string => (e.code === 'FORBIDDEN' && e.reasonCode === undefined ? t('offers.refusal.forbidden') : t('offers.error', { code: fail(e) })),
    [t, fail],
  );
  const failText = useCallback((e: { code: string; reasonCode?: string; detail?: string }): string => (e.reasonCode !== undefined || e.detail !== undefined ? fail(e) : refusal(e)), [fail, refusal]);

  const load = useCallback(
    async (m: string): Promise<void> => {
      const mine = ++seq.current;
      const target = m.trim();
      if (target === '' || !mayView) {
        setLoaded(target);
        setView(undefined);
        return;
      }
      const res = await runtime.activeIncentives.list(target);
      if (mine !== seq.current) return;
      setLoaded(target);
      if (res.ok) {
        setView(res.value);
        setError(undefined);
      } else {
        // A refused read keeps the list unknown: the screen says it could not load, never "no offers".
        setError(refusal(res.error));
      }
      // D494 (L7): the merchants' Smart Campaigns of the same market; a refused read leaves the panel out, never an empty list.
      const camps = await runtime.smartCampaigns.list(target);
      if (mine === seq.current) setCampaigns(camps.ok ? camps.value : undefined);
      const zones = await runtime.ops.incentiveZones(target);
      if (mine !== seq.current || !zones.ok) return;
      setZoneNames(new Map(groupIncentiveZones(zones.value.zones).filter((z) => z.approved !== undefined).map((z) => [z.zoneId, (z.approved ?? z.latest).definition.name])));
    },
    [runtime, mayView, refusal],
  );

  useEffect(() => {
    setView(undefined);
    setCampaigns(undefined);
    setError(undefined);
    setDraft(EMPTY_OFFER_DRAFT);
    setPercentText('');
    void load(market);
    // Reload when the header scope names another market.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  useAutoRefresh(() => load(market), { intervalMs: AUTO_REFRESH_MS.queue, enabled: mayView && market !== '' && !busy });

  const tz = marketTimeZone(loaded, markets);
  const when = (iso: string | null | undefined): string => (iso === null || iso === undefined ? '—' : formatMarketInstant(iso, tz, lang));
  const money = useCallback(
    (m: { readonly amountMinor: string; readonly currency: string } | null | undefined): string =>
      m === null || m === undefined ? '—' : /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: tz ?? 'UTC', measurement: 'metric' }) : '—',
    [lang, tz],
  );
  const policy = view?.riderDiscountPolicy;
  const currency = policy?.currency ?? '';
  const canOffer = view !== undefined && policy?.state === 'ACTIVE' && view.fund.state === 'ACTIVE';
  const issue = canOffer && policy !== undefined && view !== undefined ? offerDraftIssue(draft, policy, view.proposalReasons) : undefined;
  const windows = useMemo(() => (policy !== undefined ? windowChoices(policy.maxWindowHours) : []), [policy]);
  const drawnZones = useMemo(() => [...zoneNames.entries()].sort((a, b) => a[1].localeCompare(b[1])), [zoneNames]);
  const zoneLabel = (zoneId: string): string => (zoneId === 'MARKET' ? t('offers.zone.market') : zoneNames.has(zoneId) ? t('offers.zone.drawn', { name: zoneNames.get(zoneId) ?? '' }) : t('offers.zone.unknown'));

  const propose = async (): Promise<void> => {
    if (issue !== undefined || !canOffer) return;
    setBusy(true);
    const res = await runtime.activeIncentives.propose(offerProposalBody(loaded, draft, new Date().toISOString()));
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: failText(res.error) });
    setDraft(EMPTY_OFFER_DRAFT);
    setPercentText('');
    setMsg({ tone: 'success', text: t('offers.propose.sent') });
    await load(loaded);
  };

  const decide = async (o: ActiveIncentiveView, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    const reason = (why[o.incentiveId] ?? '').trim();
    if (decision === 'REJECT' && reason.length < 3) return setMsg({ tone: 'warning', text: t('offers.decision.reasonNeeded') });
    setBusy(true);
    const res = await runtime.activeIncentives.decide(o.incentiveId, { marketId: loaded, decision, ...(reason !== '' ? { reason } : {}) });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: failText(res.error) });
      await load(loaded);
      return;
    }
    setMsg({ tone: 'success', text: t(decision === 'APPROVE' ? 'offers.decision.approved' : 'offers.decision.rejected') });
    await load(loaded);
  };

  const endNow = async (o: ActiveIncentiveView): Promise<void> => {
    setBusy(true);
    const res = await runtime.activeIncentives.end(o.incentiveId, { marketId: loaded, reasonCode: why[`end:${o.incentiveId}`] ?? 'OPERATOR_KILL_SWITCH' });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: failText(res.error) });
      await load(loaded);
      return;
    }
    setMsg({ tone: 'success', text: t('offers.end.done') });
    await load(loaded);
  };

  const endCampaign = async (c: SmartCampaignView, reasonCode: SmartCampaignEndReason): Promise<void> => {
    setBusy(true);
    const res = await runtime.smartCampaigns.end(c.campaignId, { marketId: loaded, reasonCode });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: failText(res.error) });
      await load(loaded);
      return;
    }
    setMsg({ tone: 'success', text: t('smartCampaign.end.done') });
    await load(loaded);
  };

  const offerText = (o: ActiveIncentiveView): string =>
    'subsidyBp' in o.value
      ? t('offers.offer.subsidy', { lever: v('active_incentive.lever', o.lever), percent: bpToPercentText(o.value.subsidyBp), cap: money(o.value.perOrderCap) })
      : 'guaranteePerHour' in o.value
      ? t('offers.offer.guarantee', { lever: v('active_incentive.lever', o.lever), rate: money(o.value.guaranteePerHour), minutes: o.value.minOnlineMinutes, acceptance: bpToPercentText(o.value.minAcceptanceBp) })
      : 'discountBp' in o.value
      ? t('offers.offer.line', { lever: v('active_incentive.lever', o.lever), percent: bpToPercentText(o.value.discountBp), cap: money(o.value.perRideCap) })
      : t('offers.offer.boost', { lever: v('active_incentive.lever', o.lever), amount: money(o.value.boost) });

  const actionCell = (o: ActiveIncentiveView): ReactElement => {
    const block = offerApproveBlock(o, me?.id, mayApprove, selfApproval);
    if (block === undefined) {
      return (
        <Stack gap={2}>
          <TextInput aria-label={t('offers.decision.reason')} placeholder={t('offers.decision.reason')} value={why[o.incentiveId] ?? ''} maxLength={500} onChange={(ev) => setWhy((r) => ({ ...r, [o.incentiveId]: ev.target.value }))} />
          <Row gap={2} align="center" wrap>
            <Button type="button" variant="success" disabled={busy} onClick={() => void decide(o, 'APPROVE')}>{t('offers.decision.approve')}</Button>
            <Button type="button" variant="danger" disabled={busy} onClick={() => void decide(o, 'REJECT')}>{t('offers.decision.reject')}</Button>
          </Row>
        </Stack>
      );
    }
    if (block === 'OWN_PROPOSAL' || block === 'NO_PERMISSION') return <Text role="caption" tone="tertiary">{block === 'OWN_PROPOSAL' ? t('offers.approve.own') : t('offers.approve.noPermission')}</Text>;
    if (offerCanEnd(o, mayManage)) {
      return (
        <Stack gap={2}>
          <Select aria-label={t('offers.end.reason')} value={why[`end:${o.incentiveId}`] ?? 'OPERATOR_KILL_SWITCH'} onChange={(ev) => setWhy((r) => ({ ...r, [`end:${o.incentiveId}`]: ev.target.value }))}>
            {(view?.endReasons ?? []).map((r) => (
              <option key={r} value={r}>{v('active_incentive.reason', r)}</option>
            ))}
          </Select>
          <Button type="button" variant="danger" disabled={busy} onClick={() => void endNow(o)}>{t('offers.end.button')}</Button>
        </Stack>
      );
    }
    const reason = o.endReason ?? o.decisionReason;
    return <span>{o.endReason !== null ? v('active_incentive.reason', o.endReason) : (reason ?? '—')}</span>;
  };

  return (
    <>
      <PageHeader title={t('offers.title')} subtitle={t('offers.subtitle')} />
      {!mayView ? (
        <Panel>{note(t('offers.noPermission'))}</Panel>
      ) : (
        <>
          {(error !== undefined || msg !== undefined) && (
            <Panel>
              {error !== undefined && <Notice tone="danger">{error}</Notice>}
              {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
            </Panel>
          )}

          {scopeMarket === undefined && <ScopeMarketNotice choices={choices} />}

          {view !== undefined && policy !== undefined && (
            <>
              <Panel>
                <Stack gap={3}>
                  <h3>{t('offers.policy.title')}</h3>
                  <Row gap={2} align="center" wrap>
                    <Text role="caption" tone="tertiary">{t('offers.policy.fund')}</Text>
                    <Badge tone={policyStateTone(view.fund.state)}>{v('incentive_fund.policy_state', view.fund.state)}</Badge>
                    <Text role="caption" tone="tertiary">{t('offers.policy.lever')}</Text>
                    <Badge tone={policyStateTone(policy.state)}>{v('incentive_fund.policy_state', policy.state)}</Badge>
                    <Text role="caption" tone="tertiary">{t('offers.policy.version', { version: policy.policyVersion })}</Text>
                  </Row>
                  {!canOffer ? (
                    <Notice tone="warning">{t('offers.policy.needs')}</Notice>
                  ) : (
                    <Text role="caption" tone="tertiary">
                      <span className="tnum">
                        {t('offers.policy.bounds', {
                          percent: bpToPercentText(policy.maxDiscountBp), cap: money(policy.maxDiscountPerRide), day: policy.maxPerRiderPerDay, month: policy.maxPerRiderPerMonth, hours: policy.maxWindowHours,
                          control: bpToPercentText(policy.controlGroupBp), categories: policy.serviceCategories.length,
                        })}
                      </span>
                    </Text>
                  )}
                </Stack>
              </Panel>

              <Panel padded={false}>
                <Stack gap={3}>
                  <h3>{t('offers.list.title')}</h3>
                  <Table
                    columns={[
                      { key: 'offer', header: t('offers.col.offer'), wrap: true, width: 'minmax(14rem, 1.6fr)', render: (o: ActiveIncentiveView) => { const full = `${offerText(o)} · ${v('active_incentive.reason', o.reasonCode)}`; return <span className="tnum" title={full}>{full}</span>; } },
                      { key: 'zone', header: t('offers.col.zone'), render: (o: ActiveIncentiveView) => <span>{zoneLabel(o.zoneId)}</span> },
                      { key: 'window', header: t('offers.col.window'), wrap: true, render: (o: ActiveIncentiveView) => { const line = t('offers.window.line', { from: when(o.validFrom), to: when(o.endedAt ?? o.validTo) }); return <span className="tnum" title={line}>{line}</span>; } },
                      {
                        key: 'budget', header: t('offers.col.budget'), numeric: true,
                        render: (o: ActiveIncentiveView) => (
                          <Stack gap={1}>
                            <Meter label={t('offers.col.budget')} value={offerSpendBp(o)} max={10_000} showMax={false} warnAt={0.8} dangerAt={1} formatValue={(x) => `${Math.floor(x / 100)}%`} />
                            <Text role="caption" tone="tertiary"><span className="tnum">{t('offers.budget.line', { spent: money(o.spent), cap: money(o.spendCap) })}</span></Text>
                          </Stack>
                        ),
                      },
                      { key: 'state', header: t('offers.col.state'), render: (o: ActiveIncentiveView) => <Badge tone={offerStateTone(o.state)}>{v('active_incentive.state', o.state)}</Badge> },
                      { key: 'by', header: t('offers.col.by'), render: (o: ActiveIncentiveView) => <span>{t('offers.by.line', { who: o.createdBy.label ?? personName(o.createdBy.id) ?? tailOf(o.createdBy.id), when: when(o.createdAt) })}</span> },
                      // Wide enough for the longest end reason AND its chevron: a narrower track clipped the select until it read like a label (D854, L6c-F10).
                      { key: 'action', header: t('offers.col.action'), width: 'minmax(16rem, 1fr)', render: actionCell },
                    ]}
                    rows={view.incentives}
                    rowKey={(o) => o.incentiveId}
                    empty={note(t('offers.empty'))}
                  />
                </Stack>
              </Panel>

              <GuaranteeOffersPanel
                view={view}
                market={loaded}
                mayManage={mayManage}
                busy={busy}
                submit={async (body) => {
                  setBusy(true);
                  const res = await runtime.activeIncentives.propose(body);
                  setBusy(false);
                  if (!res.ok) {
                    setMsg({ tone: 'danger', text: failText(res.error) });
                    return false;
                  }
                  setMsg({ tone: 'success', text: t('offers.propose.sent') });
                  await load(loaded);
                  return true;
                }}
              />

              <DeliverySubsidyOffersPanel
                view={view}
                market={loaded}
                mayManage={mayManage}
                busy={busy}
                zones={drawnZones}
                submit={async (body) => {
                  setBusy(true);
                  const res = await runtime.activeIncentives.propose(body);
                  setBusy(false);
                  if (!res.ok) {
                    setMsg({ tone: 'danger', text: failText(res.error) });
                    return false;
                  }
                  setMsg({ tone: 'success', text: t('offers.propose.sent') });
                  await load(loaded);
                  return true;
                }}
              />

              <SmartCampaignsPanel view={campaigns} mayManage={mayManage} busy={busy} when={when} onEnd={endCampaign} />

              {mayManage && (
                <Panel>
                  <Stack gap={3}>
                    <h3>{t('offers.propose.title')}</h3>
                    {!canOffer ? (
                      <Text role="caption" tone="tertiary">{t('offers.propose.needsPolicy')}</Text>
                    ) : (
                      <FormRow action={<Button type="button" disabled={issue !== undefined || busy} onClick={() => void propose()}>{t('offers.propose.submit')}</Button>}>
                        <Field label={t('offers.propose.zone')} htmlFor="offer-zone">
                          <Select id="offer-zone" value={draft.zoneId} onChange={(ev) => setDraft((d) => ({ ...d, zoneId: ev.target.value }))}>
                            <option value="MARKET">{t('offers.zone.market')}</option>
                            {drawnZones.map(([id, name]) => (
                              <option key={id} value={id}>{t('offers.zone.drawn', { name })}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('offers.propose.discount')} htmlFor="offer-discount" hint={t('offers.propose.discount.hint', { percent: bpToPercentText(policy.maxDiscountBp) })}>
                          <TextInput
                            id="offer-discount"
                            inputMode="decimal"
                            value={percentText}
                            maxLength={6}
                            onChange={(ev) => {
                              setPercentText(ev.target.value);
                              setDraft((d) => ({ ...d, discountBp: percentTextToBp(ev.target.value) }));
                            }}
                          />
                        </Field>
                        {isIso4217Currency(currency) && (
                          <>
                            <MoneyInput id="offer-per-ride" label={t('offers.propose.perRide')} currency={currency} locale={lang} valueMinor={draft.perRideCapMinor} onChangeMinor={(minor) => setDraft((d) => ({ ...d, perRideCapMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
                            <MoneyInput id="offer-budget" label={t('offers.propose.budget')} currency={currency} locale={lang} valueMinor={draft.spendCapMinor} onChangeMinor={(minor) => setDraft((d) => ({ ...d, spendCapMinor: minor }))} issueText={(i) => v('format.money_input_issue', i)} />
                          </>
                        )}
                        <Field label={t('offers.propose.window')} htmlFor="offer-window">
                          <Select id="offer-window" value={draft.windowHours === undefined ? '' : String(draft.windowHours)} onChange={(ev) => setDraft((d) => ({ ...d, windowHours: ev.target.value === '' ? undefined : Number.parseInt(ev.target.value, 10) }))}>
                            <option value="">{t('offers.propose.window.choose')}</option>
                            {windows.map((h) => (
                              <option key={h} value={String(h)}>{t('offers.propose.window.hours', { hours: h })}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('offers.propose.reason')} htmlFor="offer-reason">
                          <Select id="offer-reason" value={draft.reasonCode} onChange={(ev) => setDraft((d) => ({ ...d, reasonCode: ev.target.value }))}>
                            <option value="">{t('offers.propose.reason.choose')}</option>
                            {view.proposalReasons.map((r) => (
                              <option key={r} value={r}>{v('active_incentive.reason', r)}</option>
                            ))}
                          </Select>
                        </Field>
                      </FormRow>
                    )}
                    {canOffer && issue !== undefined && draft !== EMPTY_OFFER_DRAFT && <Text role="caption" tone="tertiary">{t(`offers.issue.${issue}`)}</Text>}
                  </Stack>
                </Panel>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
